import React, { useEffect, useMemo, useState, useRef } from "react";
import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";
import { useNavigate, useParams, Link } from "react-router-dom";
import { allAPiServicesCall } from "../../services/apiServices";
import { getAuthConfig, getFormDataConfig } from "../../services/apiUtils";
import { htmlToText } from "../../utils/utils";
import CommonLayout from "../../layouts/CommonLayout";
import {
    FaMusic,
    FaEdit,
    FaUser,
    FaCompactDisc,
    FaMicrophone,
    FaFileAlt,
    FaLink,
    FaImage,
    FaCalendarAlt,
    FaClock,
    FaHashtag,
    FaKey,
    FaTags,
    FaToggleOn,
    FaSave,
    FaArrowLeft,
    FaExclamationCircle,
    FaCheck,
    FaInfoCircle,
    FaCloudUploadAlt,
    FaGuitar,
    FaHeadphones,
    FaEye,
    FaCheckCircle
} from "react-icons/fa";
import "./EditSong.css";

function csvToArray(str) {
    return (str || "").split(",").map(s => s.trim()).filter(Boolean);
}

const toNumberOrNull = (v) => (v === "" || v === undefined || v === null ? null : Number(v));

export default function EditSongPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Dropdown data
    const [artists, setArtists] = useState([]);
    const [albums, setAlbums] = useState([]);
    const [loadingArtists, setLoadingArtists] = useState(false);
    const [loadingAlbums, setLoadingAlbums] = useState(false);

    // Selected IDs
    const [artist_id, setArtistId] = useState("");
    const [album_id, setAlbumId] = useState("");

    // Search state for artist dropdown
    const [artistSearchQuery, setArtistSearchQuery] = useState("");
    const [showArtistDropdown, setShowArtistDropdown] = useState(false);
    const [selectedArtistName, setSelectedArtistName] = useState("");
    const artistDropdownRef = useRef(null);

    // Core identity/content
    const [song_name, setSongName] = useState("");
    const [title, setTitle] = useState("");
    const [lyrics, setLyrics] = useState("");
    const [description, setDescription] = useState("");
    const [song_meaning, setSongMeaning] = useState("");

    // Media / URLs
    const [audioPreviewUrl, setAudioPreviewUrl] = useState("");
    const [musicVideoUrl, setMusicVideoUrl] = useState("");
    const [coverImagePath, setCoverImagePath] = useState("");
    const [newCoverImagePath, setNewCoverImagePath] = useState("");
    const [imageLoading, setImageLoading] = useState(false);
    const [uploadErr, setUploadErr] = useState(null);

    // Dates / numbers
    const [releaseDate, setReleaseDate] = useState("");
    const [song_duration, setSongDuration] = useState("");
    const [bpm, setBpm] = useState("");
    const [musicalKey, setMusicalKey] = useState("");

    // Arrays (CSV)
    const [genres, setGenres] = useState("");
    const [subGenres, setSubGenres] = useState("");
    const [writers, setWriters] = useState("");
    const [producers, setProducers] = useState("");
    const [tags, setTags] = useState("");
    const [moods, setMoods] = useState("");

    // Flags
    const [explicit, setExplicit] = useState(false);
    const [isSingle, setIsSingle] = useState(false);
    const [isFeatured, setIsFeatured] = useState(false);
    const [isTrending, setIsTrending] = useState(false);
    const [includeInMusicVideoSection, setIncludeInMusicVideoSection] = useState(false);

    // Song metadata
    const [songId, setSongId] = useState("");
    const [createdAt, setCreatedAt] = useState("");

    // Upload cover image
    const handleSongImageChange = async (e) => {
        try {
            setImageLoading(true);
            const file = e.target.files?.[0];
            if (!file) return;

            if (file.size > 5 * 1024 * 1024) {
                setUploadErr("Image size should not exceed 5 MB.");
                return;
            }

            const options = {
                maxSizeMB: 1,
                maxWidthOrHeight: 1024,
                useWebWorker: true,
                fileType: file.type,
            };

            const compressed = await imageCompression(file, options);
            const formData = new FormData();
            formData.append("image", compressed);

            const response = await allAPiServicesCall.uploadSongImage(formData, getFormDataConfig(), navigate);
            const path = response?.data?.path || response?.path || "";
            if (!path) throw new Error("No path returned from upload API");
            setNewCoverImagePath(path);
            setUploadErr(null);
        } catch (err) {
            console.error(err);
            setUploadErr("Failed to Upload Song Image.");
        } finally {
            setImageLoading(false);
        }
    };

    // Debounced search for artists
    useEffect(() => {
        if (artistSearchQuery.trim()) {
            const timer = setTimeout(async () => {
                try {
                    setLoadingArtists(true);
                    const res = await allAPiServicesCall.listArtistsSimple(
                        { limit: 40, sort: "artist_name", q: artistSearchQuery },
                        getAuthConfig()
                    );
                    const rows = Array.isArray(res?.data) ? res.data : [];
                    setArtists(rows);
                } catch (e) {
                    console.error(e);
                    toast.error("Failed to search artists");
                } finally {
                    setLoadingArtists(false);
                }
            }, 500);
            return () => clearTimeout(timer);
        } else {
            (async () => {
                try {
                    setLoadingArtists(true);
                    const res = await allAPiServicesCall.listArtistsSimple(
                        { limit: 40, sort: "artist_name" },
                        getAuthConfig()
                    );
                    const rows = Array.isArray(res?.data) ? res.data : [];
                    setArtists(rows);
                } catch (e) {
                    console.error(e);
                    toast.error("Failed to load artists");
                } finally {
                    setLoadingArtists(false);
                }
            })();
        }
    }, [artistSearchQuery]);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (artistDropdownRef.current && !artistDropdownRef.current.contains(event.target)) {
                setShowArtistDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Load song details
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const res = await allAPiServicesCall.getSongById(id, getAuthConfig());
                if (!res.success) {
                    toast.error(res?.message || "Failed to load song");
                    navigate("/all-songs");
                    return;
                }
                const song = res.data;

                setSongId(song.song_id || id);
                setCreatedAt(song.createdAt || "");

                setSongName(song.song_name || "");
                setTitle(song.title || "");
                setLyrics(htmlToText(song.lyrics || ""));
                setDescription(song.description || "");
                setSongMeaning(song.song_meaning || "");

                setAudioPreviewUrl(song.audioPreviewUrl || "");
                setMusicVideoUrl(song.musicVideoUrl || "");
                setCoverImagePath(song.coverImage || song.song_image_downloaded_link || "");

                const parsedDate = song.releaseDate ? new Date(song.releaseDate) : null;
                setReleaseDate(parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate.toISOString().substring(0, 10) : "");
                setSongDuration(song.song_duration || "");
                setBpm(song.bpm || "");
                setMusicalKey(song.key || "");

                const toCSV = (v) => Array.isArray(v) ? v.join(", ") : (typeof v === "string" ? v : "");
                setGenres(toCSV(song.genre));
                setSubGenres(toCSV(song.subGenres));
                setWriters(toCSV(song.writers));
                setProducers(toCSV(song.producers));
                setTags(toCSV(song.tags));
                setMoods(toCSV(song.moods));

                setExplicit(!!song.explicit);
                setIsSingle(!!song.isSingle);
                setIsFeatured(!!song.isFeatured);
                setIsTrending(!!song.isTrending);
                setIncludeInMusicVideoSection(!!song.includeInMusicVideoSection);

                setArtistId(song.artist_id ?? "");
                setAlbumId(song.album_id ?? "");

                if (song.artist_id) {
                    setSelectedArtistName(song.artist_name || song.artist || "");
                }
            } catch (e) {
                console.error(e);
                toast.error("Error loading song");
                navigate("/all-songs");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, navigate]);

    // Load albums when artist changes
    useEffect(() => {
        const idNum = Number(artist_id);
        if (!idNum) {
            setAlbums([]);
            return;
        }
        (async () => {
            try {
                setLoadingAlbums(true);
                const res = await allAPiServicesCall.listAlbumsByArtist(
                    idNum,
                    { sort: "album_name" },
                    getAuthConfig()
                );
                const rows = Array.isArray(res?.data) ? res.data : [];
                setAlbums(rows);
            } catch (e) {
                console.error(e);
                toast.error("Failed to load albums");
            } finally {
                setLoadingAlbums(false);
            }
        })();
    }, [artist_id]);

    const onSubmit = async (e) => {
        e.preventDefault();
        const artistIdNum = toNumberOrNull(artist_id);
        if (!song_name && !title) return toast.error("Please enter at least a Song Name or Title");
        if (!artistIdNum) return toast.error("Please select an Artist");

        const albumIdNum = toNumberOrNull(album_id) || undefined;

        setSubmitting(true);
        try {
            const form = new FormData();

            form.append("artist_id", String(artistIdNum));
            if (albumIdNum !== undefined) form.append("album_id", String(albumIdNum));

            if (song_name != null) form.append("song_name", song_name.trim());
            if (title != null) form.append("title", title.trim());

            if (lyrics != null) form.append("lyrics", lyrics);
            if (description != null) form.append("description", description);
            if (song_meaning != null) form.append("song_meaning", song_meaning);

            if (audioPreviewUrl != null) form.append("audioPreviewUrl", audioPreviewUrl.trim());
            if (musicVideoUrl != null) form.append("musicVideoUrl", musicVideoUrl.trim());
            if (newCoverImagePath) form.append("coverImage", newCoverImagePath);

            if (releaseDate) form.append("releaseDate", new Date(releaseDate).toISOString());
            if (song_duration) form.append("song_duration", String(song_duration));
            if (bpm) form.append("bpm", bpm);
            if (musicalKey) form.append("key", musicalKey);

            const genresArr = csvToArray(genres);
            const subGenresArr = csvToArray(subGenres);
            const writersArr = csvToArray(writers);
            const producersArr = csvToArray(producers);
            const tagsArr = csvToArray(tags);
            const moodsArr = csvToArray(moods);

            if (genresArr) form.append("genre", JSON.stringify(genresArr));
            if (subGenresArr) form.append("subGenres", JSON.stringify(subGenresArr));
            if (writersArr) form.append("writers", JSON.stringify(writersArr));
            if (producersArr) form.append("producers", JSON.stringify(producersArr));
            if (tagsArr) form.append("tags", JSON.stringify(tagsArr));
            if (moodsArr) form.append("moods", JSON.stringify(moodsArr));

            form.append("explicit", String(explicit));
            form.append("isSingle", String(isSingle));
            form.append("isFeatured", String(isFeatured));
            form.append("isTrending", String(isTrending));
            form.append("includeInMusicVideoSection", String(includeInMusicVideoSection));
            form.append("hasLyrics", String(!!lyrics));

            const res = await allAPiServicesCall.updateSong(id, form, getAuthConfig());
            const ok = res?.success ?? res?.data?.success ?? false;
            if (ok) {
                toast.success("Song updated successfully!");
                navigate("/all-songs");
            } else {
                const message = res?.message || res?.data?.message || "Failed to update song";
                toast.error(message);
            }
        } catch (err) {
            console.error(err);
            toast.error("Error updating song");
        } finally {
            setSubmitting(false);
        }
    };

    const albumOptions = useMemo(
        () => albums.map(al => ({
            value: al.album_id,
            label: al.album_name || al.albumName || `Album ${al.album_id}`
        })),
        [albums]
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        try {
            return new Date(dateStr).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric"
            });
        } catch {
            return null;
        }
    };

    if (loading) {
        return (
            <CommonLayout>
                <div className="add-song-page">
                    <div className="edit-song-loading">
                        <div className="edit-song-loading-spinner"></div>
                        <p className="edit-song-loading-text">Loading song details...</p>
                    </div>
                </div>
            </CommonLayout>
        );
    }

    return (
        <CommonLayout>
            <div className="add-song-page">
                {/* Back Button */}
                <button className="add-song-back-btn" onClick={() => navigate("/all-songs")}>
                    <FaArrowLeft /> Back to All Songs
                </button>

                {/* Header Section */}
                <div className="add-song-header">
                    <div className="add-song-header-content">
                        <div className="add-song-header-icon">
                            <FaEdit />
                        </div>
                        <div className="add-song-header-text">
                            <h1 className="add-song-title">
                                <FaMusic style={{ color: "#EC4899" }} />
                                Edit Song
                                <span className="edit-song-id-badge">#{songId}</span>
                            </h1>
                            <p className="add-song-subtitle">
                                Update song details and information
                            </p>
                        </div>
                    </div>
                    <div className="add-song-stats">
                        <div className="add-song-stat-card">
                            <FaCheck className="add-song-stat-icon" />
                            <div className="add-song-stat-info">
                                <span className="add-song-stat-value">{lyrics ? "Yes" : "No"}</span>
                                <span className="add-song-stat-label">Has Lyrics</span>
                            </div>
                        </div>
                        {createdAt && (
                            <div className="add-song-stat-card">
                                <FaCalendarAlt className="add-song-stat-icon" />
                                <div className="add-song-stat-info">
                                    <span className="add-song-stat-value" style={{ fontSize: "0.85rem" }}>{formatDate(createdAt)}</span>
                                    <span className="add-song-stat-label">Created</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div style={{ marginBottom: "1.5rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                    <Link to={`/lyrics/${songId}`} className="edit-song-view-link">
                        <FaEye /> View Song Details
                    </Link>
                </div>

                {/* Form */}
                <form onSubmit={onSubmit}>
                    <div className="add-song-form-container">
                        {/* Artist & Album Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaUser />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Artist & Album</h2>
                                    <p className="add-song-section-subtitle">Select the artist and optionally an album</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid">
                                {/* Artist Search */}
                                <div className="add-song-form-group" ref={artistDropdownRef}>
                                    <label className="add-song-label">
                                        Artist <span className="add-song-label-required">*</span>
                                    </label>
                                    <div className="add-song-artist-search">
                                        <div className="add-song-input-wrapper">
                                            <input
                                                type="text"
                                                className="add-song-input"
                                                placeholder="Search for an artist..."
                                                value={selectedArtistName || artistSearchQuery}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setArtistSearchQuery(value);
                                                    setSelectedArtistName("");
                                                    setArtistId("");
                                                    setShowArtistDropdown(true);
                                                }}
                                                onFocus={() => setShowArtistDropdown(true)}
                                                required
                                                autoComplete="off"
                                            />
                                            {loadingArtists && <div className="add-song-input-spinner"></div>}
                                        </div>

                                        {showArtistDropdown && artists.length > 0 && (
                                            <div className="add-song-artist-dropdown">
                                                {artists.map(artist => (
                                                    <div
                                                        key={artist.artist_id}
                                                        className="add-song-artist-option"
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            setArtistId(artist.artist_id);
                                                            setSelectedArtistName(artist.artist_name || artist.artist || `Artist ${artist.artist_id}`);
                                                            setArtistSearchQuery("");
                                                            setShowArtistDropdown(false);
                                                        }}
                                                    >
                                                        <div className="add-song-artist-option-avatar">
                                                            <FaUser />
                                                        </div>
                                                        <span className="add-song-artist-option-name">
                                                            {artist.artist_name || artist.artist || `Artist ${artist.artist_id}`}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {showArtistDropdown && artists.length === 0 && !loadingArtists && artistSearchQuery && (
                                            <div className="add-song-artist-dropdown">
                                                <div className="add-song-artist-no-results">
                                                    No artists found for "{artistSearchQuery}"
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Album Select */}
                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Album <span className="add-song-label-optional">(optional)</span>
                                    </label>
                                    <select
                                        className="add-song-select"
                                        value={album_id}
                                        onChange={(e) => setAlbumId(e.target.value)}
                                        disabled={!artist_id || loadingAlbums}
                                    >
                                        <option value="">
                                            {artist_id
                                                ? (loadingAlbums ? "Loading albums..." : "Select album (optional)")
                                                : "Select artist first"}
                                        </option>
                                        {albumOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Song Identity Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaMicrophone />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Song Identity</h2>
                                    <p className="add-song-section-subtitle">Basic information about the song</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid">
                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Song Name <span className="add-song-label-required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="Enter song name..."
                                        value={song_name}
                                        onChange={e => setSongName(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Title <span className="add-song-label-optional">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="Enter title..."
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Content Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaFileAlt />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Content</h2>
                                    <p className="add-song-section-subtitle">Lyrics, description, and meaning</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid single-column">
                                <div className="add-song-form-group add-song-form-full">
                                    <label className="add-song-label">
                                        <FaEdit /> Lyrics
                                    </label>
                                    <textarea
                                        className="add-song-textarea lyrics-textarea"
                                        placeholder="Enter song lyrics..."
                                        value={lyrics}
                                        onChange={e => setLyrics(e.target.value)}
                                        rows={8}
                                    />
                                </div>

                                <div className="add-song-form-group add-song-form-full">
                                    <label className="add-song-label">Description</label>
                                    <textarea
                                        className="add-song-textarea"
                                        placeholder="Enter song description..."
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        rows={4}
                                    />
                                </div>

                                <div className="add-song-form-group add-song-form-full">
                                    <label className="add-song-label">Song Meaning</label>
                                    <textarea
                                        className="add-song-textarea"
                                        placeholder="What does this song mean?"
                                        value={song_meaning}
                                        onChange={e => setSongMeaning(e.target.value)}
                                        rows={4}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Media Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaLink />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Media & Links</h2>
                                    <p className="add-song-section-subtitle">Audio preview, video, and cover image</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid">
                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaHeadphones /> Audio Preview URL
                                    </label>
                                    <input
                                        type="url"
                                        className="add-song-input"
                                        placeholder="https://..."
                                        value={audioPreviewUrl}
                                        onChange={e => setAudioPreviewUrl(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaCompactDisc /> Music Video URL
                                    </label>
                                    <input
                                        type="url"
                                        className="add-song-input"
                                        placeholder="https://youtube.com/..."
                                        value={musicVideoUrl}
                                        onChange={e => setMusicVideoUrl(e.target.value)}
                                    />
                                </div>

                                {/* Current Cover Image */}
                                <div className="add-song-form-group add-song-form-full">
                                    <label className="add-song-label">
                                        <FaImage /> Current Cover Image
                                    </label>
                                    <div className="edit-song-image-preview">
                                        {coverImagePath ? (
                                            <img
                                                src={coverImagePath}
                                                alt="Current cover"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = "/no-image.png";
                                                }}
                                            />
                                        ) : (
                                            <div className="edit-song-image-preview-placeholder">
                                                <FaMusic />
                                            </div>
                                        )}
                                        <div className="edit-song-image-info">
                                            <span className="edit-song-image-status">
                                                {coverImagePath ? "Image uploaded" : "No image set"}
                                            </span>
                                            <span className="edit-song-image-hint">
                                                Upload a new image below to replace
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Upload New Cover Image */}
                                <div className="add-song-form-group add-song-form-full">
                                    <label className="add-song-label">
                                        <FaCloudUploadAlt /> Replace Cover Image
                                    </label>
                                    <div className={`add-song-file-upload ${newCoverImagePath ? 'has-file' : ''}`}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleSongImageChange}
                                        />
                                        {newCoverImagePath ? (
                                            <div className="add-song-file-preview">
                                                <img
                                                    src={newCoverImagePath}
                                                    alt="New cover preview"
                                                    className="add-song-file-preview-image"
                                                />
                                                <div className="add-song-file-preview-info">
                                                    <p className="add-song-file-preview-name">New image ready</p>
                                                    <span className="add-song-file-preview-change">Click to change</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <FaCloudUploadAlt className="add-song-file-upload-icon" />
                                                <p className="add-song-file-upload-text">
                                                    Click or drag to upload new cover image
                                                </p>
                                                <p className="add-song-file-upload-hint">
                                                    PNG, JPG up to 5MB. Will be compressed to 1MB.
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    {imageLoading && (
                                        <div className="add-song-image-loading">
                                            <div className="add-song-image-loading-spinner"></div>
                                            <span className="add-song-image-loading-text">Uploading image...</span>
                                        </div>
                                    )}
                                    {uploadErr && (
                                        <div className="add-song-error">
                                            <FaExclamationCircle />
                                            {uploadErr}
                                        </div>
                                    )}
                                    {newCoverImagePath && (
                                        <div className="edit-song-new-image-badge">
                                            <FaCheckCircle /> New image will be saved on update
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Technical Details Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaClock />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Technical Details</h2>
                                    <p className="add-song-section-subtitle">Release date, duration, and audio properties</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid three-columns">
                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaCalendarAlt /> Release Date
                                    </label>
                                    <input
                                        type="date"
                                        className="add-song-input"
                                        value={releaseDate}
                                        onChange={e => setReleaseDate(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaClock /> Duration (seconds)
                                    </label>
                                    <input
                                        type="number"
                                        className="add-song-input"
                                        placeholder="e.g., 240"
                                        value={song_duration}
                                        onChange={e => setSongDuration(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaHashtag /> BPM
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., 120"
                                        value={bpm}
                                        onChange={e => setBpm(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaKey /> Musical Key
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., C#m, G major"
                                        value={musicalKey}
                                        onChange={e => setMusicalKey(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Categorization Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaTags />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Categorization</h2>
                                    <p className="add-song-section-subtitle">Genres, tags, moods, and credits</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid">
                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Genres <span className="add-song-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., Pop, Rock, R&B"
                                        value={genres}
                                        onChange={e => setGenres(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Sub-Genres <span className="add-song-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., Pop Rock, Indie"
                                        value={subGenres}
                                        onChange={e => setSubGenres(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaGuitar /> Writers <span className="add-song-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., John Doe, Jane Smith"
                                        value={writers}
                                        onChange={e => setWriters(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Producers <span className="add-song-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., Max Martin"
                                        value={producers}
                                        onChange={e => setProducers(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Tags <span className="add-song-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., love, summer, dance"
                                        value={tags}
                                        onChange={e => setTags(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Moods <span className="add-song-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., happy, energetic, romantic"
                                        value={moods}
                                        onChange={e => setMoods(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Flags Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaToggleOn />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Visibility & Flags</h2>
                                    <p className="add-song-section-subtitle">Special attributes for the song</p>
                                </div>
                            </div>

                            <div className="add-song-flags-grid">
                                <label className={`add-song-flag ${explicit ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={explicit}
                                        onChange={(e) => setExplicit(e.target.checked)}
                                    />
                                    <div className="add-song-flag-toggle"></div>
                                    <div className="add-song-flag-content">
                                        <span className="add-song-flag-label">Explicit</span>
                                        <span className="add-song-flag-description">Contains explicit content</span>
                                    </div>
                                </label>

                                <label className={`add-song-flag ${isSingle ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={isSingle}
                                        onChange={(e) => setIsSingle(e.target.checked)}
                                    />
                                    <div className="add-song-flag-toggle"></div>
                                    <div className="add-song-flag-content">
                                        <span className="add-song-flag-label">Single</span>
                                        <span className="add-song-flag-description">Released as a single</span>
                                    </div>
                                </label>

                                <label className={`add-song-flag ${isFeatured ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={isFeatured}
                                        onChange={(e) => setIsFeatured(e.target.checked)}
                                    />
                                    <div className="add-song-flag-toggle"></div>
                                    <div className="add-song-flag-content">
                                        <span className="add-song-flag-label">Featured</span>
                                        <span className="add-song-flag-description">Show in featured section</span>
                                    </div>
                                </label>

                                <label className={`add-song-flag ${isTrending ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={isTrending}
                                        onChange={(e) => setIsTrending(e.target.checked)}
                                    />
                                    <div className="add-song-flag-toggle"></div>
                                    <div className="add-song-flag-content">
                                        <span className="add-song-flag-label">Trending</span>
                                        <span className="add-song-flag-description">Show in trending section</span>
                                    </div>
                                </label>

                                <label className={`add-song-flag ${includeInMusicVideoSection ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={includeInMusicVideoSection}
                                        onChange={(e) => setIncludeInMusicVideoSection(e.target.checked)}
                                    />
                                    <div className="add-song-flag-toggle"></div>
                                    <div className="add-song-flag-content">
                                        <span className="add-song-flag-label">Music Video</span>
                                        <span className="add-song-flag-description">Include in video section</span>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Submit Section */}
                        <div className="add-song-submit-section">
                            <div className="add-song-submit-hint">
                                <FaInfoCircle />
                                <span>Fields marked with * are required</span>
                            </div>
                            <div className="add-song-submit-actions">
                                <button
                                    type="button"
                                    className="add-song-btn add-song-btn-secondary"
                                    onClick={() => navigate("/all-songs")}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="add-song-btn add-song-btn-primary"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>Saving...</>
                                    ) : (
                                        <>
                                            <FaSave /> Update Song
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </CommonLayout>
    );
}
